// Network stats for the top of NetworkView. DERIVED from the assembled data
// rather than hardcoded, so they always reflect the real seed. Shared by
// `buildFixtures` (the static bundle, keyed off the fixed seed anchor) and
// the store's `recomputeTodayStats` action (after the journey replay lands on
// top, keyed off the live sim clock) so there is exactly one derivation and
// the two can never drift apart.

import { isSameDay } from 'date-fns';
import type {
  Booking,
  ConfirmationCall,
  CustomerRecord,
  Deposit,
  SalePayment,
} from '@/domain/types';

export interface TodayStats {
  deposits: number;
  callsRecovered: number;
  crossStudioRoutes: number;
  inChairRebookings: number;
  newCustomers: number;
  networkRevenueAud: number;
}

export interface TodayStatsInput {
  deposits: Record<string, Deposit>;
  confirmationCalls: Record<string, ConfirmationCall>;
  customers: Record<string, CustomerRecord>;
  bookings: Record<string, Booking>;
  salePayments: Record<string, SalePayment>;
  /** The day "today" means for this derivation. */
  todayIso: string;
}

/** Derive `todayStats` from the current records, anchored on `todayIso`. */
export function deriveTodayStats(input: TodayStatsInput): TodayStats {
  const today = new Date(input.todayIso);
  return {
    // Deposits paid today, across the whole network.
    deposits: Object.values(input.deposits).filter(
      (d) => d.paidAt && isSameDay(new Date(d.paidAt), today),
    ).length,
    // Confirmation calls that landed a confirmation (a recovered sitting).
    callsRecovered: Object.values(input.confirmationCalls).filter(
      (call) => call.outcome === 'confirmed',
    ).length,
    // Customers who have been routed to another studio at least once.
    crossStudioRoutes: Object.values(input.customers).filter(
      (c) => c.crossStudioTouches.length > 0,
    ).length,
    // Rebookings captured while the client was still in the chair.
    inChairRebookings: Object.values(input.bookings).filter(
      (b) => b.source === 'in-chair-rebooking',
    ).length,
    // Customers whose very first touch is today.
    newCustomers: Object.values(input.customers).filter(
      (c) => c.source?.firstTouchAt && isSameDay(new Date(c.source.firstTouchAt), today),
    ).length,
    // Session + merch money taken across the network today.
    networkRevenueAud: Object.values(input.salePayments)
      .filter((s) => isSameDay(new Date(s.takenAt), today))
      .reduce((sum, s) => sum + s.balanceAud + s.merchAud, 0),
  };
}
